import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "Paws on Record";
const SITE_URL = "https://pawsonrecord.com";
const DEFAULT_DESC =
  "Paws on Record — a free U.S. registry of convicted animal abusers, sourced from public government records and verified court convictions. Search by city, state, or 50-mile radius before you adopt, foster, or rehome an animal.";
const DEFAULT_IMG = `${SITE_URL}/og-default.png`;

/**
 * Per-page SEO + Open Graph + JSON-LD.
 *
 * Props:
 *   title         (string)        — page-specific title; will be suffixed with " · Paws on Record"
 *   description   (string)        — meta description (≤160 chars)
 *   path          (string)        — canonical path, defaults to current pathname
 *   image         (string)        — absolute OG image URL
 *   noindex       (bool)          — set true for admin/utility pages
 *   jsonLd        (object|array)  — structured data; will be JSON-stringified
 */
export default function SEO({
  title,
  description = DEFAULT_DESC,
  path,
  image = DEFAULT_IMG,
  noindex = false,
  jsonLd,
}) {
  const fullTitle = title
    ? `${title} · ${SITE_NAME}`
    : `${SITE_NAME} · U.S. Convicted Animal Abuser Registry`;
  const canonicalPath = path || (typeof window !== "undefined" ? window.location.pathname : "/");
  const canonical = `${SITE_URL}${canonicalPath}`;
  const desc = (description || DEFAULT_DESC).slice(0, 300);

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonical} />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={image} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

/**
 * Build a schema.org JSON-LD payload for an abuser detail page.
 * Uses NewsArticle (the registry is functioning as journalism documenting
 * a public conviction) with `about: { @type: Person, name, address }`.
 * This is what gives Google the "rich result" eligibility.
 */
export function abuserJsonLd(d, canonicalUrl) {
  if (!d) return null;
  const headline = `${d.name} — ${d.crime_type || "Animal Cruelty Conviction"} · ${d.city || ""}, ${d.state_code || d.state || ""}`.replace(/, $/, "");
  const desc = (d.crime_summary || d.description || "").slice(0, 240);
  const isConvicted = (d.status || "").toLowerCase().includes("convict") || (d.status || "").toLowerCase().includes("guilty");
  return [
    {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline,
      description: desc,
      datePublished: d.conviction_year ? `${d.conviction_year}-01-01` : undefined,
      url: canonicalUrl,
      image: d.photo_url || undefined,
      author: {
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_URL,
      },
      publisher: {
        "@type": "Organization",
        name: SITE_NAME,
        url: SITE_URL,
        logo: {
          "@type": "ImageObject",
          url: `${SITE_URL}/icon.svg`,
        },
      },
      about: {
        "@type": "Person",
        name: d.name,
        ...(d.photo_url ? { image: d.photo_url } : {}),
        address: {
          "@type": "PostalAddress",
          addressLocality: d.city || undefined,
          addressRegion: d.state || d.state_code || undefined,
          addressCountry: "US",
        },
      },
      mentions: [
        {
          "@type": "Thing",
          name: d.crime_type || "Animal cruelty",
        },
      ],
      ...(isConvicted
        ? {
            articleSection: "Animal Abuse Convictions",
          }
        : { articleSection: "Animal Abuse Cases" }),
      isAccessibleForFree: true,
    },
  ];
}

export const homeJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
  description: DEFAULT_DESC,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export const orgJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/icon.svg`,
  description:
    "A free U.S. public registry of convicted animal abusers. Sourced from government registries and verified news convictions.",
  sameAs: ["https://www.facebook.com/PawsOnRecord"],
};
